import type { RawGqlCompany } from './types';
import { getDefaultCompany } from '../useGridCompanies/utils';
import type { Company } from '../useGridCompanies/utils';
import { getBestValuationRatio } from '@/utilities/getBestValuationRatio';
import {
  formatterCurrency,
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';

export const companyGqlMapper = (company: RawGqlCompany | null): Company => {
  if (!company) {
    return getDefaultCompany();
  }

  const industrySlug = company.primaryIndustry?.slug;
  const industryLabel = company.primaryIndustry?.name ?? 'n/a';

  const yearFounded = company.info?.yearFounded;
  const dividendYield = company.analysisDividend?.dividendYield;
  const marketCap = company.analysisValue?.marketCap;
  const currencyISO = company.listingCurrencyISO || 'USD';

  const return1D = company.analysisValue?.return1d;

  const return7D = company.analysisValue?.return7d;

  const return1YrAbs = company.analysisValue?.return1yAbs;
  const analystPriceTarget = company.analysisValue?.priceTarget;
  const bestValuationRatio = getBestValuationRatio(
    company.analysisValue?.pb || null,
    company.analysisValue?.pe || null,
    company.analysisValue?.priceToSales || null,
    industryLabel
  );

  return {
    ...getDefaultCompany(),
    canonicalUrl: company.canonicalURL ?? 'n/a',
    companyId: company.id ?? 'n/a',
    shortDescription: company.info?.shortDescription ?? 'n/a',
    mainThumb: company.info?.coverSmallUrl ?? 'n/a',
    exchangeSymbol: company.exchangeSymbol ?? 'n/a',
    shortName: company.name ?? 'n/a',
    legalName: company.info?.legalName ?? 'n/a',
    slug: company.nameSlug ?? 'n/a',
    website: company.info?.url ?? 'n/a',
    snowflakePoints: [
      (company.score?.value || 0) + 1,
      (company.score?.future || 0) + 1,
      (company.score?.past || 0) + 1,
      (company.score?.health || 0) + 1,
      (company.score?.dividend || 0) + 1,
    ],
    tickerSymbol: company.tickerSymbol ?? 'n/a',
    uniqueSymbol: company.uniqueSymbol ?? 'n/a',
    logoUrl: company.info?.logoUrl ?? 'n/a',
    yearFounded: !yearFounded ? 'n/a' : yearFounded.toString(),
    return1D: return1D ?? null,
    return1DFormatted: formatterPercentage(return1D),
    return7D: return7D ?? null,
    return7DFormatted: formatterPercentage(return7D),
    return1YrAbs: return1YrAbs ?? null,
    return1YrAbsFormatted: formatterPercentage(return1YrAbs),
    marketCap: marketCap ?? null,
    marketCapFormatted: formatterCurrencyTruncated(marketCap, currencyISO, 1),
    sharePrice: company.analysisValue?.lastSharePrice ?? null,
    sharePriceFormatted: formatterCurrency(
      company.analysisValue?.lastSharePrice,
      currencyISO
    ),
    analystsPriceTarget: analystPriceTarget ?? null,
    analystsPriceTargetFormatted: formatterCurrency(
      analystPriceTarget,
      currencyISO
    ),
    bestValuationRatio,
    peFormatted: formatterFactor(company.analysisValue?.pe),
    pbFormatted: formatterFactor(company.analysisValue?.pb),
    psFormatted: formatterFactor(company.analysisValue?.priceToSales),
    analystCount: company.analysisMisc?.analystCount || null,
    growthEstimate: company.analysisFuture?.netIncomeGrowthAnnual ?? null,
    growthEstimateFormatted: formatterPercentage(
      company.analysisFuture?.netIncomeGrowthAnnual
    ),
    earningsGrowth: company.analysisFuture?.netIncomeGrowthAnnual ?? null,
    earningsGrowthFormatted: formatterPercentage(
      company.analysisFuture?.netIncomeGrowthAnnual
    ),
    salesGrowth: company.analysisFuture?.revenueGrowthAnnual ?? null,
    salesGrowthFormatted: formatterPercentage(
      company.analysisFuture?.revenueGrowthAnnual
    ),
    dividendYield: dividendYield ?? null,
    dividendYieldFormatted: formatterPercentage(dividendYield),
    industrySlug: industrySlug || 'n/a',
    industryLabel,
    isRecentSearchEntry: company.isRecentSearchEntry,
  };
};
